// תצוגת באנר דרך אותו runtime שמשמש ביצוא (BBRuntime.mount). לתמונות ממוזערות ולתצוגות השוואה.
import { useEffect, useRef } from 'react';
import type { BannerDoc } from '@bb/schema';
import type { Controller } from '@bb/renderer';
import { endFrameElapsed, type AssetMap } from './types';

/** טוען @font-face לנכסי פונט שבמפה — פעם אחת לכל נכס */
export function ensureFonts(assets: AssetMap) {
  if (typeof document === 'undefined') return;
  for (const [id, a] of Object.entries(assets)) {
    if (a.kind !== 'font' || !a.family || !a.url) continue;
    const elId = `bbfont-${id}`;
    if (document.getElementById(elId)) continue;
    const st = document.createElement('style');
    st.id = elId;
    const fmt = a.mime?.includes('woff2') ? 'woff2' : a.mime?.includes('woff') ? 'woff' : a.mime?.includes('otf') ? 'opentype' : a.mime?.includes('ttf') || a.mime?.includes('truetype') ? 'truetype' : 'woff2';
    st.textContent = `@font-face{font-family:"${a.family.replace(/"/g, '')}";src:url("${a.url}") format("${fmt}");font-weight:${a.weight ?? 400};font-display:block;${a.unicodeRange ? `unicode-range:${a.unicodeRange};` : ''}}`;
    document.head.appendChild(st);
  }
}

interface Props {
  doc: BannerDoc;
  assets: AssetMap;
  /** זמן להצגה (elapsed); 'end' = פריים הסיום */
  at?: number | 'end';
  /** ניגון אוטומטי (תצוגת השוואה) */
  autoplay?: boolean;
  /** קנה מידה לתצוגה בלבד — אינו משנה את הקובץ */
  scale?: number;
  /** מפתח לטעינה מחדש / ניגון חוזר */
  replayKey?: number;
  label?: string;
}

export default function BannerView({ doc, assets, at = 'end', autoplay = false, scale = 1, replayKey = 0, label }: Props) {
  const root = useRef<HTMLDivElement>(null);
  const ctl = useRef<Controller | null>(null);
  useEffect(() => {
    if (!root.current || !globalThis.BBRuntime) return;
    ensureFonts(assets);
    const c = globalThis.BBRuntime.mount(root.current, doc, assets, { autoplay: false });
    ctl.current = c;
    if (autoplay) c.play();
    else c.seek(at === 'end' ? endFrameElapsed(doc) : at);
    // פונטים נטענים באיחור — רינדור חוזר כשהם מוכנים
    let alive = true;
    document.fonts?.ready.then(() => { if (alive && !autoplay) c.seek(at === 'end' ? endFrameElapsed(doc) : at); });
    return () => { alive = false; c.destroy(); ctl.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, assets, autoplay, replayKey]);
  useEffect(() => {
    if (ctl.current && !autoplay) ctl.current.seek(at === 'end' ? endFrameElapsed(doc) : at);
  }, [at, doc, autoplay]);
  return (
    <div className="bv-frame" dir="ltr" style={{ width: doc.width * scale, height: doc.height * scale }} role="img" aria-label={label ?? `באנר ${doc.width}x${doc.height}`}>
      <div style={{ transform: `scale(${scale})`, transformOrigin: "0 0", width: doc.width, height: doc.height }}>
        <div ref={root} />
      </div>
    </div>
  );
}

/** קנה מידה שמתאים באנר לתיבה */
export function fitScale(w: number, h: number, maxW: number, maxH: number) {
  return Math.min(1, maxW / w, maxH / h);
}
